import { useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CUISINE_OPTIONS, DIETARY_RESTRICTION_OPTIONS } from '@meal-planner/shared';
import { api } from '../services/api.js';
import { useProfile } from '../hooks/useAuth.js';
import { useTheme, type Theme } from '../contexts/ThemeContext.js';
import { Logo } from '../components/ui/Logo.js';
import { Icon } from '../components/ui/Icon.js';
import { Chip } from '../components/ui/Chip.js';
import { Button } from '../components/ui/Button.js';
import { FamilyRosterField, type FamilyMember } from '../components/FamilyRosterField.js';

const TOTAL_STEPS = 5;

const EQUIPMENT_OPTIONS = ['stovetop', 'oven', 'air_fryer', 'instant_pot', 'slow_cooker', 'microwave'] as const;

const SKILL_LEVELS = [
  { value: 'easy',         label: 'Beginner',  hint: 'Quick and forgiving' },
  { value: 'intermediate', label: 'Confident', hint: 'Comfortable improvising' },
  { value: 'expert',       label: 'Advanced',  hint: 'Bring on the projects' },
] as const;

type FamilySizeBucket = 'just_me' | 'small' | 'large';

const FAMILY_BUCKETS: { value: FamilySizeBucket; label: string; size: number; figure: string }[] = [
  { value: 'just_me', label: 'Just me',     size: 1, figure: '1' },
  { value: 'small',   label: '2 – 4 people', size: 2, figure: '2–4' },
  { value: 'large',   label: '5 or more',   size: 5, figure: '5+' },
];

const THEMES: { value: Theme; label: string; swatch: string }[] = [
  { value: 'emerald', label: 'Emerald', swatch: 'oklch(0.74 0.14 162)' },
  { value: 'tomato',  label: 'Tomato',  swatch: 'oklch(0.70 0.17 30)'  },
  { value: 'saffron', label: 'Saffron', swatch: 'oklch(0.80 0.15 75)'  },
  { value: 'plum',    label: 'Plum',    swatch: 'oklch(0.65 0.16 320)' },
  { value: 'cobalt',  label: 'Cobalt',  swatch: 'oklch(0.68 0.16 240)' },
];

function formatLabel(s: string) {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const inputStyle: CSSProperties = {
  background: 'var(--surface-2)',
  border: '1px solid var(--line-soft)',
  borderRadius: 12,
  padding: '10px 14px',
  fontSize: 14,
  color: 'var(--ink)',
  fontFamily: 'inherit',
  outline: 'none',
};

export function OnboardingPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: profile } = useProfile();
  const { theme, setTheme } = useTheme();

  const [step, setStep] = useState(1);
  const [familyBucket, setFamilyBucket] = useState<FamilySizeBucket>(() => {
    const size = profile?.familySize ?? 2;
    if (size === 1) return 'just_me';
    if (size >= 5) return 'large';
    return 'small';
  });
  const [budget, setBudget] = useState(String(profile?.budget ?? 200));
  const [zipCode, setZipCode] = useState(profile?.zipCode ?? '');
  const [dietary, setDietary] = useState<string[]>(profile?.dietaryRestrictions ?? []);
  const [cuisines, setCuisines] = useState<string[]>(profile?.cuisinePreferences ?? []);
  const [equipment, setEquipment] = useState<string[]>(((profile as any)?.equipment as string[] | undefined) ?? []);
  const [skillLevel, setSkillLevel] = useState<string>(((profile as any)?.skillLevel as string | undefined) ?? 'easy');
  const [busyNightMode, setBusyNightMode] = useState<boolean>(((profile as any)?.busyNightMode as boolean | undefined) ?? false);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>(
    (profile?.familyMembers ?? []) as FamilyMember[],
  );
  const [useFamilyRoster, setUseFamilyRoster] = useState<boolean>(
    !!profile?.familyMembers?.length,
  );
  const [selectedTheme, setSelectedTheme] = useState<Theme>(profile?.theme ?? theme);
  const [saving, setSaving] = useState(false);

  const isReturning = profile?.onboardingComplete === true;

  function toggleChip<T extends string>(arr: T[], setArr: (v: T[]) => void, val: T) {
    setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  }

  function familySizeFromBucket(): number {
    return FAMILY_BUCKETS.find((b) => b.value === familyBucket)?.size ?? 2;
  }

  async function finish() {
    setSaving(true);
    try {
      await api.put('/api/profile', {
        familySize: familySizeFromBucket(),
        budget: parseFloat(budget) || 200,
        dietaryRestrictions: dietary,
        cuisinePreferences: cuisines,
        zipCode: zipCode.match(/^\d{5}$/) ? zipCode : '10001',
        onboardingComplete: true,
        theme: selectedTheme,
        equipment,
        skillLevel,
        busyNightMode,
        familyMembers: useFamilyRoster && familyMembers.length > 0 ? familyMembers : null,
      } as any);
      setTheme(selectedTheme);
      await qc.invalidateQueries({ queryKey: ['profile'] });
      navigate('/dashboard', { replace: true });
    } catch {
      await qc.invalidateQueries({ queryKey: ['profile'] });
      navigate('/dashboard', { replace: true });
    } finally {
      setSaving(false);
    }
  }

  async function skip() {
    setSaving(true);
    try {
      await api.patch('/api/profile/settings', { onboardingComplete: true });
      await qc.invalidateQueries({ queryKey: ['profile'] });
    } finally {
      setSaving(false);
      navigate('/dashboard', { replace: true });
    }
  }

  function StepNav({ onNext, nextLabel = 'Continue', canSkip = true }: {
    onNext?: () => void;
    nextLabel?: string;
    canSkip?: boolean;
  }) {
    const isLast = step === TOTAL_STEPS;
    return (
      <div className="flex items-center justify-between mt-10">
        {step > 1 ? (
          <button type="button" onClick={() => setStep((s) => s - 1)}
            className="text-sm hover:underline inline-flex items-center gap-1.5"
            style={{ color: 'var(--ink-3)' }}>
            <Icon name="chev" size={12} style={{ transform: 'rotate(180deg)' }} />
            Back
          </button>
        ) : <span />}

        <div className="flex items-center gap-3">
          {canSkip && !isLast && (
            <button type="button" onClick={skip} disabled={saving}
              className="text-sm hover:underline disabled:opacity-50"
              style={{ color: 'var(--ink-4)' }}>
              Skip for now
            </button>
          )}
          <Button type="button"
            onClick={isLast ? finish : (onNext ?? (() => setStep((s) => s + 1)))}
            disabled={saving}>
            {saving ? 'Saving…' : isLast ? 'Finish setup' : nextLabel}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{
        background: 'var(--bg-deep)',
        backgroundImage: 'radial-gradient(60% 40% at 50% 0%, var(--accent-soft), transparent 70%)',
      }}
    >
      <div
        className="w-full max-w-lg p-8 sm:p-10"
        style={{
          background: 'var(--surface)',
          borderRadius: 22,
          border: '1px solid var(--line-soft)',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        {/* ── Brand mark + progress bar ─────────────────────────────── */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <Logo />
            <span className="text-xs font-mono" style={{ color: 'var(--ink-4)' }}>
              {String(step).padStart(2, '0')} / {String(TOTAL_STEPS).padStart(2, '0')}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => (
              <div
                key={i}
                className="h-1 flex-1 rounded-full transition-all duration-300"
                style={{
                  background: i < step ? 'var(--accent)' : 'var(--surface-3)',
                }}
              />
            ))}
          </div>
        </div>

        {/* ── Step 1: Basics ─────────────────────────────────────────── */}
        {step === 1 && (
          <div>
            <h1 className="h-display" style={{ fontSize: 32, lineHeight: 1.1, margin: 0, color: 'var(--ink)' }}>
              {isReturning
                ? <>Update your <span style={{ color: 'var(--accent)', fontStyle: 'italic', fontWeight: 400 }}>basics.</span></>
                : <>Welcome{profile?.name ? <>, <span style={{ color: 'var(--accent)', fontStyle: 'italic', fontWeight: 400 }}>{profile.name.split(' ')[0]}</span></> : <> <span style={{ color: 'var(--accent)', fontStyle: 'italic', fontWeight: 400 }}>in.</span></>}</>
              }
            </h1>
            <p className="text-sm mt-3 mb-8" style={{ color: 'var(--ink-3)' }}>
              {isReturning ? 'Change your household setup any time.' : "Let's personalize your meal plans. This takes about 2 minutes."}
            </p>

            <div className="mb-6">
              <p className="mb-3" style={{ color: 'var(--ink-2)', fontSize: 13, fontWeight: 500 }}>
                How many people are you planning for?
              </p>
              <div className="grid grid-cols-3 gap-3">
                {FAMILY_BUCKETS.map((b) => {
                  const active = familyBucket === b.value;
                  return (
                    <button key={b.value} type="button"
                      onClick={() => setFamilyBucket(b.value)}
                      className="text-center transition-all"
                      style={{
                        borderRadius: 14,
                        border: `1.5px solid ${active ? 'var(--accent)' : 'var(--line-soft)'}`,
                        background: active ? 'var(--accent-soft)' : 'var(--surface-2)',
                        padding: '14px 8px',
                        cursor: 'pointer',
                      }}
                    >
                      <div className="h-display" style={{ fontSize: 26, fontWeight: 500, color: 'var(--ink)', marginBottom: 4 }}>
                        {b.figure}
                      </div>
                      <div style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--ink-2)' }}>{b.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div
              className="mb-6 p-4"
              style={{
                background: 'var(--surface-2)',
                borderRadius: 14,
                border: '1px solid var(--line-soft)',
              }}
            >
              <FamilyRosterField
                enabled={useFamilyRoster}
                onEnabledChange={setUseFamilyRoster}
                members={familyMembers}
                onMembersChange={setFamilyMembers}
              />
            </div>

            <div className="mb-5">
              <label className="block mb-1" style={{ color: 'var(--ink-2)', fontSize: 13, fontWeight: 500 }}>
                Weekly grocery budget ($)
              </label>
              <input
                type="number" min={10} max={10000} step={10}
                value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="200"
                style={{ ...inputStyle, width: 160 }}
              />
            </div>

            <div>
              <label className="block mb-1" style={{ color: 'var(--ink-2)', fontSize: 13, fontWeight: 500 }}>
                ZIP code
              </label>
              <input
                type="text" pattern="\d{5}" maxLength={5}
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value.replace(/\D/g, ''))}
                placeholder="10001"
                style={{ ...inputStyle, width: 130 }}
              />
              <p className="text-xs mt-1.5" style={{ color: 'var(--ink-4)' }}>
                Used for local grocery pricing estimates.
              </p>
            </div>

            <StepNav />
          </div>
        )}

        {/* ── Step 2: Dietary ────────────────────────────────────────── */}
        {step === 2 && (
          <div>
            <h1 className="h-display" style={{ fontSize: 32, lineHeight: 1.1, margin: 0, color: 'var(--ink)' }}>
              Dietary{' '}
              <span style={{ color: 'var(--accent)', fontStyle: 'italic', fontWeight: 400 }}>needs.</span>
            </h1>
            <p className="text-sm mt-3 mb-8" style={{ color: 'var(--ink-3)' }}>
              Select everything that applies to your household. We'll make sure every meal fits.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {DIETARY_RESTRICTION_OPTIONS.map((opt) => (
                <Chip key={opt} on={dietary.includes(opt)}
                  onClick={() => toggleChip(dietary, setDietary, opt)}>
                  {formatLabel(opt)}
                </Chip>
              ))}
            </div>
            {dietary.length > 0 ? (
              <button type="button" onClick={() => setDietary([])}
                className="text-xs hover:underline" style={{ color: 'var(--ink-4)' }}>
                Clear all
              </button>
            ) : (
              <p className="text-xs" style={{ color: 'var(--ink-4)' }}>
                Nothing selected = no restrictions. That's fine!
              </p>
            )}
            <StepNav />
          </div>
        )}

        {/* ── Step 3: Cuisines ───────────────────────────────────────── */}
        {step === 3 && (
          <div>
            <h1 className="h-display" style={{ fontSize: 32, lineHeight: 1.1, margin: 0, color: 'var(--ink)' }}>
              Cuisine{' '}
              <span style={{ color: 'var(--accent)', fontStyle: 'italic', fontWeight: 400 }}>preferences.</span>
            </h1>
            <p className="text-sm mt-3 mb-8" style={{ color: 'var(--ink-3)' }}>
              Pick your favorites. We'll weight suggestions toward what your family loves.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {CUISINE_OPTIONS.map((opt) => (
                <Chip key={opt} on={cuisines.includes(opt)}
                  onClick={() => toggleChip(cuisines, setCuisines, opt)}>
                  {formatLabel(opt)}
                </Chip>
              ))}
            </div>
            {cuisines.length > 0 && (
              <button type="button" onClick={() => setCuisines([])}
                className="text-xs hover:underline" style={{ color: 'var(--ink-4)' }}>
                Clear all
              </button>
            )}
            <StepNav />
          </div>
        )}

        {/* ── Step 4: Cooking — Phase B ───────────────────────────────── */}
        {step === 4 && (
          <div>
            <h1 className="h-display" style={{ fontSize: 32, lineHeight: 1.1, margin: 0, color: 'var(--ink)' }}>
              How do you{' '}
              <span style={{ color: 'var(--accent)', fontStyle: 'italic', fontWeight: 400 }}>cook?</span>
            </h1>
            <p className="text-sm mt-3 mb-6" style={{ color: 'var(--ink-3)' }}>
              We'll only suggest recipes that match your gear and pace. You can change all of this later.
            </p>

            <p className="mb-2 text-sm font-medium" style={{ color: 'var(--ink-2)' }}>What's in your kitchen?</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {EQUIPMENT_OPTIONS.map((opt) => (
                <Chip key={opt} on={equipment.includes(opt)}
                  onClick={() => toggleChip(equipment, setEquipment, opt)}>
                  {formatLabel(opt)}
                </Chip>
              ))}
            </div>

            <p className="mb-2 text-sm font-medium" style={{ color: 'var(--ink-2)' }}>Comfort level in the kitchen</p>
            <div className="grid grid-cols-3 gap-2 mb-6">
              {SKILL_LEVELS.map((s) => {
                const active = skillLevel === s.value;
                return (
                  <button key={s.value} type="button"
                    onClick={() => setSkillLevel(s.value)}
                    className="text-left transition-all"
                    style={{
                      borderRadius: 12,
                      border: `1.5px solid ${active ? 'var(--accent)' : 'var(--line-soft)'}`,
                      background: active ? 'var(--accent-soft)' : 'var(--surface-2)',
                      padding: '12px 14px',
                      cursor: 'pointer',
                    }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: active ? 'var(--accent)' : 'var(--ink)' }}>
                      {s.label}
                    </div>
                    <div className="mt-0.5" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{s.hint}</div>
                  </button>
                );
              })}
            </div>

            <button type="button"
              onClick={() => setBusyNightMode((v) => !v)}
              className="w-full text-left transition-all flex items-start gap-3"
              style={{
                borderRadius: 12,
                border: `1.5px solid ${busyNightMode ? 'var(--accent)' : 'var(--line-soft)'}`,
                background: busyNightMode ? 'var(--accent-soft)' : 'var(--surface-2)',
                padding: '12px 14px',
                cursor: 'pointer',
              }}>
              <span className="mt-0.5 flex-shrink-0 inline-flex items-center justify-center"
                style={{
                  width: 18, height: 18, borderRadius: 4,
                  border: `1.5px solid ${busyNightMode ? 'var(--accent)' : 'var(--line-soft)'}`,
                  background: busyNightMode ? 'var(--accent)' : 'transparent',
                  color: 'white',
                }}>
                {busyNightMode && <Icon name="check" size={11} stroke={3} />}
              </span>
              <span>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>Busy nights</div>
                <div className="mt-0.5" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                  Lean toward 20-minute meals on every weekday by default.
                </div>
              </span>
            </button>

            <StepNav />
          </div>
        )}

        {/* ── Step 5: Theme ──────────────────────────────────────────── */}
        {step === 5 && (
          <div>
            <h1 className="h-display" style={{ fontSize: 32, lineHeight: 1.1, margin: 0, color: 'var(--ink)' }}>
              Make it{' '}
              <span style={{ color: 'var(--accent)', fontStyle: 'italic', fontWeight: 400 }}>yours.</span>
            </h1>
            <p className="text-sm mt-3 mb-8" style={{ color: 'var(--ink-3)' }}>
              Pick an accent. You can change this — and toggle dark mode — any time from the profile menu.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {THEMES.map((t) => {
                const active = selectedTheme === t.value;
                return (
                  <button key={t.value} type="button"
                    onClick={() => { setSelectedTheme(t.value); setTheme(t.value); }}
                    className="flex items-center gap-3 transition-all"
                    style={{
                      borderRadius: 14,
                      border: `1.5px solid ${active ? t.swatch : 'var(--line-soft)'}`,
                      background: 'var(--surface-2)',
                      padding: '14px 16px',
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      className="rounded-full flex-shrink-0"
                      style={{
                        width: 28, height: 28,
                        background: t.swatch,
                        boxShadow: active ? `0 0 0 4px color-mix(in oklab, ${t.swatch} 25%, transparent)` : undefined,
                      }}
                    />
                    <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--ink)' }}>{t.label}</span>
                    {active && (
                      <span className="ml-auto inline-flex items-center" style={{ color: t.swatch }}>
                        <Icon name="check" size={14} stroke={2.6} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <StepNav canSkip={false} />
          </div>
        )}
      </div>
    </div>
  );
}

